/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import * as queryString from "query-string";
import { isNotEmptyObj } from "~/tools";
import { QueryState, useQueryState } from "../useQueryState/useQueryState";
import { getTypedKeys } from "./types";

function buildFilters<T>(
  queryState: QueryState &
    T & {
      limit: number;
      page: number;
    },
  filterKeys: (keyof QueryState | keyof T)[]
) {
  return getTypedKeys(queryState)
    .filter((key) => filterKeys.includes(key))
    .reduce((acc, curr) => {
      return {
        ...acc,
        [curr]: queryState[curr],
      };
    }, {} as T);
}

export function useFilters<
  T extends queryString.ParsedQuery<string | number | boolean>
>({ filterKeys, onApply }: { filterKeys: (keyof T)[]; onApply?: () => void }) {
  const { queryState, updateQueryState, removeQueryState } = useQueryState<T>();

  const [filters, setFilters] = useState<T>(() =>
    buildFilters(queryState, filterKeys)
  );

  useEffect(() => {
    setFilters(buildFilters(queryState, filterKeys));
  }, [queryState, filterKeys]);

  const apply = () => {
    updateQueryState({ page: 0, ...filters } as T);
    onApply?.();
  };

  const reset = () => {
    removeQueryState(getTypedKeys(filters));
    onApply?.();
  };

  return {
    apply,
    reset,
    filters,
    setFilters,
    hasFilters: isNotEmptyObj(filters),
  };
}
